import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { User, Lock, Eye, EyeOff, Loader2, Shield, ArrowLeft } from "lucide-react";
import { LOGO_URL } from "../components/Logo";
import { useTranslation } from "react-i18next";
import axios from "axios";

import { API } from "../utils/api";

// Google Icon SVG
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export default function Login() {
  const navigate = useNavigate();
  const { login, setUser, setToken } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [formData, setFormData] = useState({
    username: "",
    password: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: formData.username,
        password: formData.password
      });

      if (response.data.requires_verification) {
        // Necesita verificación 2FA
        setRequiresVerification(true);
        toast.info(t('auth.verificationSent') || "Se ha enviado un código de verificación a tu email");
      } else {
        // Login directo sin 2FA
        const { token, user } = response.data;
        localStorage.setItem("plazafy_token", token);
        localStorage.setItem("plazafy_user", JSON.stringify(user));
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        setToken(token);
        setUser(user);
        toast.success(t('auth.welcomeBack') || "¡Bienvenido de nuevo!");
        if (user?.role === "admin") {
          navigate("/admin");
        } else {
          navigate("/dashboard");
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Usuario o contraseña incorrectos";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/verify-code`, {
        username: formData.username,
        code: verificationCode
      });

      const { token, user } = response.data;
      localStorage.setItem("plazafy_token", token);
      localStorage.setItem("plazafy_user", JSON.stringify(user));
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setToken(token);
      setUser(user);
      toast.success(t('auth.verificationSuccess') || "¡Verificación exitosa!");
      if (user?.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Código inválido";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBackToLogin = () => {
    setRequiresVerification(false);
    setVerificationCode("");
  };

  // Pantalla de verificación de código
  if (requiresVerification) {
    return (
      <div className="min-h-screen bg-[#1a1625] flex flex-col items-center justify-center px-4 py-8 relative overflow-hidden">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-500/5 rounded-full blur-3xl" />
        
        <div className="relative z-10 flex flex-col items-center mb-4">
          <img 
            src={LOGO_URL} 
            alt="Plazafy" 
            className="h-40 mb-2 object-contain"
          />
        </div>

        <div className="relative z-10 w-full max-w-md bg-[#231f30] rounded-2xl shadow-2xl p-8 border border-white/5">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-violet-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-violet-400" />
            </div>
            <h2 className="text-white text-xl font-bold mb-2">{t('auth.securityVerification') || "Verificación de Seguridad"}</h2>
            <p className="text-gray-400 text-sm">
              {t('auth.codeSent') || "Hemos enviado un código de 6 dígitos a tu email. Introdúcelo para continuar."}
            </p>
          </div>

          <form onSubmit={handleVerifyCode} className="space-y-5">
            <div>
              <label className="block text-gray-300 font-medium mb-2">{t('auth.verificationCode') || "Código de verificación"}</label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                required
                maxLength={6}
                className="w-full px-4 py-4 bg-[#15121e] border border-white/10 rounded-xl text-white text-center text-2xl font-bold tracking-[0.5em] placeholder:text-gray-600 placeholder:tracking-[0.5em] focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition-all"
                data-testid="verification-code-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading || verificationCode.length !== 6}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-violet-600 hover:bg-violet-700 text-white font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-violet-500/20"
              data-testid="verify-code-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Shield className="w-5 h-5" />
              )}
              {loading ? (t('auth.verifying') || "Verificando...") : (t('auth.verifyCode') || "Verificar Código")}
            </button>
          </form>

          <button
            onClick={handleBackToLogin}
            className="w-full mt-4 flex items-center justify-center gap-2 py-3 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('auth.backToLogin') || "Volver al inicio de sesión"}
          </button>

          <p className="text-gray-500 text-xs text-center mt-4">
            {t('auth.codeExpires') || "El código expira en 10 minutos. Si no lo recibes, verifica tu carpeta de spam."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1625] flex flex-col items-center justify-center px-4 py-8 relative overflow-hidden">
      {/* Subtle glow effect */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-500/5 rounded-full blur-3xl" />
      
      {/* Logo - grande */}
      <div className="relative z-10 flex flex-col items-center mb-4">
        <img 
          src={LOGO_URL} 
          alt="Plazafy" 
          className="h-56 object-contain transition-transform duration-300 ease-out hover:scale-110 cursor-pointer"
          data-testid="login-logo"
        />
        <h1 className="text-white text-2xl font-bold mb-1 -mt-2">{t('auth.welcome')}</h1>
        <p className="text-gray-400 text-base">{t('auth.subtitle')}</p>
      </div>

      {/* Login Card - más compacto */}
      <div className="relative z-10 w-full max-w-md bg-[#231f30] rounded-2xl shadow-2xl p-6 border border-white/5">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Username Field */}
          <div>
            <label className="block text-gray-300 font-medium mb-1.5 text-sm">{t('auth.username')}</label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder={t('auth.enterUsername') || "Ingresa tu usuario"}
                required
                className="w-full pl-12 pr-4 py-3 bg-[#15121e] border border-white/10 rounded-xl text-white placeholder:text-gray-500 focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition-all"
                data-testid="login-username-input"
              />
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label className="block text-gray-300 font-medium mb-1.5 text-sm">{t('auth.password')}</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('auth.enterPassword') || "Ingresa tu contraseña"}
                required
                className="w-full pl-12 pr-12 py-3 bg-[#15121e] border border-white/10 rounded-xl text-white placeholder:text-gray-500 focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 transition-all"
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Login Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-3 bg-violet-600 hover:bg-violet-700 text-white font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-violet-500/20"
            data-testid="login-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <User className="w-5 h-5" />
            )}
            {loading ? t('auth.loggingIn') : t('auth.login')}
          </button>
        </form>

        {/* Divider */}
        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10"></div>
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="px-3 bg-[#231f30] text-gray-500">{t('auth.orContinueWith')}</span>
          </div>
        </div>

        {/* Social Login Buttons */}
        <div className="flex justify-center gap-3">
          <button
            type="button"
            onClick={() => {
              const redirectUrl = window.location.origin + '/auth/callback';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-9 h-9 flex items-center justify-center bg-[#15121e] hover:bg-violet-500/20 border border-white/10 hover:border-violet-500/50 rounded-lg transition-all"
            data-testid="google-login-btn"
            title={t('auth.continueWithGoogle')}
          >
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path fill="#8b5cf6" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#a78bfa" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#c4b5fd" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#ddd6fe" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </button>
          <button
            type="button"
            onClick={() => {
              const redirectUri = encodeURIComponent('https://plazafy.es/api/auth/apple/callback');
              const clientId = 'es.plazafy.auth';
              const scope = 'name email';
              const state = Math.random().toString(36).substring(7);
              window.location.href = `https://appleid.apple.com/auth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&response_mode=form_post&state=${state}`;
            }}
            className="w-9 h-9 flex items-center justify-center bg-[#15121e] hover:bg-violet-500/20 border border-white/10 hover:border-violet-500/50 rounded-lg transition-all"
            data-testid="apple-login-btn"
            title="Continuar con Apple"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="#a78bfa">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
          </button>
        </div>

        {/* Links */}
        <div className="mt-4 text-center space-y-1">
          <Link 
            to="/forgot-password"
            className="block text-gray-500 hover:text-violet-400 text-sm transition-colors"
          >
            {t('auth.forgotPassword')}
          </Link>
          <p className="text-gray-500 text-sm">
            {t('auth.noAccount')}{" "}
            <Link 
              to="/register" 
              className="text-violet-400 font-medium hover:text-violet-300 transition-colors"
              data-testid="go-to-register"
            >
              {t('auth.registerHere')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
